import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Badge,
    ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ChartCard from "./ChartCard";

export default function DashboardStats() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('mois'); // 'jour', 'mois', 'annee'

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stats.php?action=dashboard',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des statistiques...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchDashboard}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    // Calculs pour les pourcentages
    const totalProduits = data?.stock?.total_produits || 1;
    const produitsRupture = data?.stock?.rupture || 0;
    const produitsCritique = data?.stock?.critique || 0;
    const produitsNormaux = totalProduits - produitsRupture - produitsCritique;

    const tauxRupture = (produitsRupture / totalProduits * 100).toFixed(1);
    const tauxCritique = (produitsCritique / totalProduits * 100).toFixed(1);
    const tauxNormal = (produitsNormaux / totalProduits * 100).toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-dashboard-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Tableau de bord
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Vue d'ensemble de l'activité • {new Date().toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={fetchDashboard}
                                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                                    >
                                        <i className="ri-refresh-line"></i>
                                        <span>Actualiser</span>
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stock/etat/instantane')}>
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Sélecteur de période pour le CA */}
                    {/* <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-end">
                                <div className="btn-group" role="group">
                                    <Button 
                                        variant={period === 'jour' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setPeriod('jour')}
                                        className="rounded-start-pill"
                                    >
                                        Jour
                                    </Button>
                                    <Button 
                                        variant={period === 'mois' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setPeriod('mois')}
                                    >
                                        Mois
                                    </Button>
                                    <Button 
                                        variant={period === 'annee' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setPeriod('annee')}
                                        className="rounded-end-pill"
                                    >
                                        Année
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row> */}

                    {/* Cartes CA - Version améliorée */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span className="badge bg-white bg-opacity-25 text-white mb-2 rounded-pill px-3 py-2">
                                                <i className="ri-calendar-line me-1"></i>
                                                Aujourd'hui
                                            </span>
                                            <h3 className="display-7 fw-bold mb-0 mt-2">{formatCurrency(data?.ca_jour || 0)}</h3>
                                            <p className="text-white-50 mt-2 mb-0">
                                                <i className="ri-arrow-up-line me-1"></i>
                                                vs hier
                                            </p>
                                        </div>
                                        <div className="bg-white bg-opacity-25 rounded-circle p-3">
                                            <i className="ri-money-euro-circle-line fs-2"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span className="badge bg-white bg-opacity-25 text-white mb-2 rounded-pill px-3 py-2">
                                                <i className="ri-calendar-2-line me-1"></i>
                                                Ce mois
                                            </span>
                                            <h4 className="display-7 fw-bold mb-0 mt-2">{formatCurrency(data?.ca_mois || 0)}</h4>
                                            <p className="text-white-50 mt-2 mb-0">  
                                                <i className="ri-arrow-up-line me-1"></i>
                                                vs mois dernier
                                            </p>
                                        </div>
                                        <div className="bg-white bg-opacity-25 rounded-circle p-3">
                                            <i className="ri-bar-chart-2-line fs-2"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span className="badge bg-white bg-opacity-25 text-white mb-2 rounded-pill px-3 py-2">
                                                <i className="ri-calendar-check-line me-1"></i>
                                                Cette année
                                            </span>
                                            <h4 className="display-7 fw-bold mb-0 mt-2">{formatCurrency(data?.ca_an || 0)}</h4>
                                            <p className="text-white-50 mt-2 mb-0">
                                                <i className="ri-arrow-up-line me-1"></i>
                                                vs année dernière
                                            </p>
                                        </div>
                                        <div className="bg-white bg-opacity-25 rounded-circle p-3">
                                            <i className="ri-line-chart-line fs-2"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Statistiques générales - Version améliorée */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-primary-soft me-3">
                                            <i className="ri-group-line text-primary fs-4"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label text-muted small text-uppercase fw-semibold">Clients</span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value mb-0 fw-bold">{formatNumber(data?.clients || 0)}</h3>
                                                <Badge bg="success" className="rounded-pill">+12%</Badge>
                                            </div>
                                            <small className="text-muted">nouveaux ce mois</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-info-soft me-3">
                                            <i className="ri-truck-line text-info fs-4"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label text-muted small text-uppercase fw-semibold">Fournisseurs</span>
                                            <h3 className="stat-value mb-0 fw-bold">{formatNumber(data?.fournisseurs || 0)}</h3>
                                            <small className="text-muted">actifs</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-warning-soft me-3">
                                            <i className="ri-shopping-cart-line text-warning fs-4"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label text-muted small text-uppercase fw-semibold">Commandes en cours</span>
                                            <h3 className="stat-value mb-0 fw-bold">{data?.commandes?.en_cours || 0}</h3>
                                            <small className="text-muted">à traiter</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-success-soft me-3">
                                            <i className="ri-check-double-line text-success fs-4"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label text-muted small text-uppercase fw-semibold">Commandes reçues</span>
                                            <h3 className="stat-value mb-0 fw-bold">{data?.commandes?.recues || 0}</h3>
                                            <small className="text-muted">ce mois</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Section Stock et Valeur */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <ChartCard title="État du stock" icon="ri-stack-line">
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-danger rounded-pill px-3 py-2">Rupture</span>
                                            <span className="fw-bold">{produitsRupture} produits</span>
                                        </div>
                                        <span className="text-muted">{tauxRupture}%</span>
                                    </div>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{produitsRupture} produits en rupture sur {totalProduits}</Tooltip>}
                                    >
                                        <ProgressBar 
                                            now={parseFloat(tauxRupture)} 
                                            variant="danger" 
                                            style={{ height: '10px', borderRadius: '5px' }}
                                        />
                                    </OverlayTrigger>
                                </div>

                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-warning rounded-pill px-3 py-2">Critique</span>
                                            <span className="fw-bold">{produitsCritique} produits</span>
                                        </div>
                                        <span className="text-muted">{tauxCritique}%</span>
                                    </div>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{produitsCritique} produits sous seuil d'alerte</Tooltip>}
                                    >
                                        <ProgressBar 
                                            now={parseFloat(tauxCritique)} 
                                            variant="warning" 
                                            style={{ height: '10px', borderRadius: '5px' }}
                                        />
                                    </OverlayTrigger>
                                </div>

                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-success rounded-pill px-3 py-2">Normal</span>
                                            <span className="fw-bold">{produitsNormaux} produits</span>
                                        </div>
                                        <span className="text-muted">{tauxNormal}%</span>
                                    </div>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{produitsNormaux} produits en stock normal</Tooltip>}
                                    >
                                        <ProgressBar 
                                            now={parseFloat(tauxNormal)} 
                                            variant="success" 
                                            style={{ height: '10px', borderRadius: '5px' }}
                                        />
                                    </OverlayTrigger>
                                </div>
                            </ChartCard>
                        </Col>
                        <Col md={6}>
                            <ChartCard title="Valeur du stock" icon="ri-pie-chart-2-line">
                                <div className="text-center mb-4">
                                    <div className="position-relative d-inline-block">
                                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '120px', height: '120px', margin: '0 auto' }}>
                                            <div>
                                                <h3 className="mb-0 text-primary">{formatNumber(data?.stock?.total_produits || 0)}</h3>
                                                <small className="text-muted">produits</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <div className="bg-primary-soft rounded-3 p-3 text-center">
                                            <small className="text-muted d-block">Valeur achat</small>
                                            <h5 className="mb-0 text-primary">{formatCurrency(data?.valeur_stock?.valeur_achat || 0)}</h5>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="bg-success-soft rounded-3 p-3 text-center">
                                            <small className="text-muted d-block">Valeur vente</small>
                                            <h5 className="mb-0 text-success">{formatCurrency(data?.valeur_stock?.valeur_vente || 0)}</h5>
                                        </div>
                                    </div>
                                </div>
                                {data?.valeur_stock?.valeur_achat > 0 && (
                                    <div className="mt-3">
                                        <small className="text-muted">Marge potentielle</small>
                                        <h6 className="text-success">
                                            {formatCurrency((data?.valeur_stock?.valeur_vente || 0) - (data?.valeur_stock?.valeur_achat || 0))}
                                            <small className="text-muted ms-2">
                                                ({(((data?.valeur_stock?.valeur_vente || 0) - (data?.valeur_stock?.valeur_achat || 0)) / (data?.valeur_stock?.valeur_achat || 1) * 100).toFixed(1)}%)
                                            </small>
                                        </h6>
                                    </div>
                                )}
                            </ChartCard>
                        </Col>
                    </Row>

                    {/* Alertes et notifications */}
                    {(produitsRupture > 0 || produitsCritique > 0) && (
                        <Card className="border-0 shadow-sm mb-4 bg-light">
                            <Card.Body className="p-4">
                                <h6 className="mb-3">
                                    <i className="ri-notification-3-line me-2 text-warning"></i>
                                    Alertes stock
                                </h6>
                                <Row className="g-3">
                                    {produitsRupture > 0 && (
                                        <Col md={6}>
                                            <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                                <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
                                                    <i className="ri-close-circle-line text-danger fs-4"></i>
                                                </div>
                                                <div>
                                                    <small className="text-danger fw-bold">Rupture de stock</small>
                                                    <h5 className="mb-0">{produitsRupture} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-danger" size="sm" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails →
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    {produitsCritique > 0 && (
                                        <Col md={6}>
                                            <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                                <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                                    <i className="ri-alert-line text-warning fs-4"></i>
                                                </div>
                                                <div>
                                                    <small className="text-warning fw-bold">Stock critique</small>
                                                    <h5 className="mb-0">{produitsCritique} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-warning" size="sm" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails →
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Liens rapides vers les autres stats - Version améliorée */}
                    <h5 className="mb-3">
                        <i className="ri-apps-line me-2 text-primary"></i>
                        Analyses détaillées
                    </h5>
                    <Row className="g-3">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center quick-link-card h-100" 
                                  onClick={() => navigate('/stats/ventes/ca')}
                                  style={{ cursor: 'pointer' }}>
                                <Card.Body className="p-4">
                                    <div className="icon-wrapper bg-primary-soft mb-3">
                                        <i className="ri-money-euro-circle-line text-primary fs-2"></i>
                                    </div>
                                    <h6 className="fw-semibold">Chiffre d'affaires</h6>
                                    <small className="text-muted">Évolution et tendances</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center quick-link-card h-100" 
                                  onClick={() => navigate('/stats/ventes/top-produits')}
                                  style={{ cursor: 'pointer' }}>
                                <Card.Body className="p-4">
                                    <div className="icon-wrapper bg-success-soft mb-3">
                                        <i className="ri-star-line text-success fs-2"></i>
                                    </div>
                                    <h6 className="fw-semibold">Top produits</h6>
                                    <small className="text-muted">Les plus vendus</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center quick-link-card h-100" 
                                  onClick={() => navigate('/stats/achats/depenses')}
                                  style={{ cursor: 'pointer' }}>
                                <Card.Body className="p-4">
                                    <div className="icon-wrapper bg-warning-soft mb-3">
                                        <i className="ri-shopping-cart-2-line text-warning fs-2"></i>
                                    </div>
                                    <h6 className="fw-semibold">Dépenses achats</h6>
                                    <small className="text-muted">Analyse des coûts</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center quick-link-card h-100" 
                                  onClick={() => navigate('/stats/stock/rotation')}
                                  style={{ cursor: 'pointer' }}>
                                <Card.Body className="p-4">
                                    <div className="icon-wrapper bg-info-soft mb-3">
                                        <i className="ri-refresh-line text-info fs-2"></i>
                                    </div>
                                    <h6 className="fw-semibold">Rotation stock</h6>
                                    <small className="text-muted">Performance</small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .stat-icon-wrapper {
                        width: 48px;
                        height: 48px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 12px;
                        transition: transform 0.3s ease;
                    }

                    .stat-card:hover .stat-icon-wrapper {
                        transform: scale(1.1);
                    }

                    .bg-primary-soft {
                        background: rgba(67, 97, 238, 0.1);
                    }

                    .bg-success-soft {
                        background: rgba(16, 185, 129, 0.1);
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.1);
                    }

                    .bg-info-soft {
                        background: rgba(13, 202, 240, 0.1);
                    }

                    .quick-link-card {
                        transition: all 0.3s ease;
                    }

                    .quick-link-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(67, 97, 238, 0.2) !important;
                        background: linear-gradient(135deg, #f8fafc, #ffffff);
                    }

                    .quick-link-card .icon-wrapper {
                        width: 64px;
                        height: 64px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 16px;
                        transition: all 0.3s ease;
                    }

                    .quick-link-card:hover .icon-wrapper {
                        transform: scale(1.1) rotate(5deg);
                    }

                    .stat-label {
                        font-size: 0.7rem;
                        letter-spacing: 0.5px;
                    }

                    .progress {
                        background-color: #e9ecef;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}